import * as React from "react";
import { CheckCircle2, ChevronDown, Loader2, XCircle } from "lucide-react";
import { cn, Tooltip, TooltipContent, TooltipTrigger } from "@finster/ui";
import { SimpleMarkdown } from "../../shared/components/SimpleMarkdown";
import { parseNarratedReasoningSections } from "../../shared/agentReasoning";
import type {
  ExcelAgentDelegationView,
  ExcelAgentSubAgentView,
  ExcelAgentTimelineBlock,
  ExcelAgentTimelineState,
  ExcelAgentToolStatus,
  ExcelAgentToolView,
  ExcelAgentSubToolView,
  ExcelWorkloadProgress,
} from "../agentTimeline";
import { visibleExcelAgentHops, visibleExcelAgentTools } from "../agentTimeline";
import { getExcelAgentToolDisplayName, isExcelResearchTool } from "../agentTimelineMeta";
import { SkillActiveActivity } from "../../shared/skills/components/SkillActiveActivity";
import {
  getSnapshotPreview,
  subscribeSnapshotPreviews,
} from "../snapshotPreviewStore";

const TIMELINE_MARKDOWN_CLASS = cn(
  "[&_h1]:!text-[12.5px]",
  "[&_h2]:!text-[12px]",
  "[&_h3]:!text-[11.5px]",
  "[&_h4]:!text-[11px]",
  "[&_div]:!text-[11.5px]",
  "[&_li]:!text-[11.5px]",
  "[&_ol]:!text-[11.5px]",
  "[&_ul]:!text-[11.5px]"
);

function workloadProgressPreview(progress: ExcelWorkloadProgress): string {
  if (progress.kind === "requests") {
    return `${progress.completed}/${progress.total} requests complete`;
  }
  const outcomes =
    progress.usable !== undefined && progress.failed !== undefined
      ? ` · ${progress.usable} written · ${progress.failed} blank`
      : "";
  return `${progress.completed}/${progress.total} rows closed${outcomes}`;
}

/** Renders the status glyph used by tool pills in the timeline. */
const StatusIcon: React.FC<{ status: ExcelAgentToolStatus }> = ({ status }) => {
  if (status === "calling") return <Loader2 className="size-4 animate-spin" />;
  if (status === "failed") return <XCircle className="size-4" />;
  return <CheckCircle2 className="size-4 text-primary" />;
};

/** Renders the quiet live indicator used between dispatched tool calls. */
const WorkingPulse: React.FC = () => (
  <span
    className="relative flex h-4 w-4 items-center justify-center"
    role="status"
    aria-label="Agent working"
    data-agent-working-indicator
  >
    <span className="activity-pulse-wave absolute h-2 w-2 rounded-full bg-primary" aria-hidden />
    <span className="relative h-2 w-2 rounded-full bg-primary" aria-hidden />
  </span>
);

/** Shared pill layout for top-level and nested tool calls. */
const ToolPillBase: React.FC<{
  status: ExcelAgentToolStatus;
  label: string;
  preview?: string;
  compact?: boolean;
  wide?: boolean;
  title?: string;
}> = ({ status, label, preview, compact = false, wide = false, title }) => {
  const failed = status === "failed";
  return (
    <span
      title={title}
      className={cn(
        "inline-flex max-w-full items-center gap-1.5 rounded-full border bg-background leading-none",
        compact ? "h-6 px-2.5 text-[12px]" : "h-7 px-3 text-[13px]",
        failed ? "border-border text-muted-foreground" : "border-border",
        status === "calling" && "text-muted-foreground opacity-80"
      )}
    >
      <StatusIcon status={status} />
      <span className="shrink-0 font-medium">{label}</span>
      {preview && (
        <>
          <span className="text-muted-foreground/70">·</span>
          <span
            className={cn(
              "truncate font-normal text-muted-foreground",
              compact ? "max-w-[24ch] text-[11px]" : wide ? "max-w-[42ch] text-xs" : "max-w-[28ch] text-xs"
            )}
          >
            {preview}
          </span>
        </>
      )}
    </span>
  );
};

/** Renders a top-level executor tool call in compact timeline form. */
const ToolPill: React.FC<{ tool: ExcelAgentToolView }> = ({ tool }) => {
  const researchProgress = tool.researchGroupProgress;
  const workloadProgress = tool.workloadProgress;
  const label = getExcelAgentToolDisplayName(tool.tool);
  const preview = researchProgress
    ? `${researchProgress.completedRequests}/${researchProgress.totalRequests} requests complete`
    : workloadProgress
      ? workloadProgressPreview(workloadProgress)
      : tool.argsPreview;
  const pill = (
    <ToolPillBase
      status={tool.status}
      label={label}
      preview={preview}
      wide={tool.tool === "set_citations"}
      title={tool.argsPreview ? undefined : preview ? `${label} (${preview})` : label}
    />
  );
  const fullArgs = tool.argsPreview?.trim();
  if (!fullArgs) return pill;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex min-w-0 max-w-full">{pill}</span>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        align="start"
        sideOffset={6}
        className="max-h-48 max-w-80 overflow-y-auto whitespace-pre-wrap break-words text-xs leading-relaxed"
      >
        {fullArgs}
      </TooltipContent>
    </Tooltip>
  );
};

/** Renders a nested research subtool call. */
const SubToolPill: React.FC<{ tool: ExcelAgentSubToolView }> = ({ tool }) => {
  return (
    <ToolPillBase
      status={tool.status}
      label={getExcelAgentToolDisplayName(tool.tool)}
      preview={tool.description}
      compact
      title={
        tool.description
          ? `${getExcelAgentToolDisplayName(tool.tool)} (${tool.description})`
          : getExcelAgentToolDisplayName(tool.tool)
      }
    />
  );
};

interface TimelineTraceBlock {
  kind: "trace";
  key: string;
  hop: number;
  lastHop: number;
  thinkingText: string;
  tools: ExcelAgentToolView[];
}

interface TimelineAssistantBlock {
  kind: "assistant";
  key: string;
  hop: number;
  text: string;
}

type TimelineDisplayBlock = TimelineTraceBlock | TimelineAssistantBlock;

function appendTraceBlock(
  blocks: TimelineDisplayBlock[],
  hop: number,
  thinkingText: string,
  tools: ExcelAgentToolView[]
): void {
  if (!thinkingText.trim() && tools.length === 0) return;
  const previous = blocks.at(-1);
  if (previous?.kind === "trace") {
    previous.thinkingText = [previous.thinkingText, thinkingText.trim()]
      .filter(Boolean)
      .join("\n\n");
    previous.tools.push(...tools);
    previous.lastHop = hop;
    return;
  }
  blocks.push({
    kind: "trace",
    key: `trace-${hop}-${blocks.length}`,
    hop,
    lastHop: hop,
    thinkingText: thinkingText.trim(),
    tools: [...tools],
  });
}

function appendAssistantBlock(blocks: TimelineDisplayBlock[], hop: number, text: string): void {
  if (!text.trim()) return;
  const previous = blocks.at(-1);
  if (previous?.kind === "assistant") {
    previous.text += text;
    return;
  }
  blocks.push({
    kind: "assistant",
    key: `assistant-${hop}-${blocks.length}`,
    hop,
    text,
  });
}

function orderedHopBlocks(hop: ExcelAgentTimelineState["hops"][number]): ExcelAgentTimelineBlock[] {
  if (hop.blocks?.length) return hop.blocks;
  return [
    {
      kind: "trace",
      thinkingText: hop.thinkingText ?? "",
      toolCallIds: (hop.tools ?? []).map((tool) => tool.callId),
    },
  ];
}

/**
 * Builds one chronological stream while keeping every tool inside a reasoning
 * trace. Adjacent trace-only hops retain the established vertical folding.
 * Empty or internal-only hops are skipped so the stream matches what the
 * thought-process status counts as visible progress.
 */
function toDisplayBlocks(timeline: ExcelAgentTimelineState): TimelineDisplayBlock[] {
  const blocks: TimelineDisplayBlock[] = [];
  for (const hop of visibleExcelAgentHops(timeline)) {
    const tools = visibleExcelAgentTools(hop.tools ?? []);
    const toolsById = new Map(tools.map((tool) => [tool.callId, tool]));
    const claimedToolIds = new Set<string>();
    for (const block of orderedHopBlocks(hop)) {
      if (block.kind === "assistant") {
        appendAssistantBlock(blocks, hop.hop, block.text);
        continue;
      }
      const blockTools = block.toolCallIds.flatMap((callId) => {
        const tool = toolsById.get(callId);
        if (!tool) return [];
        claimedToolIds.add(callId);
        return [tool];
      });
      appendTraceBlock(blocks, hop.hop, block.thinkingText, blockTools);
    }
    const unclaimedTools = tools.filter((tool) => !claimedToolIds.has(tool.callId));
    appendTraceBlock(blocks, hop.hop, "", unclaimedTools);
  }
  return blocks;
}

/** Renders narrated reasoning as titled sections when the model provided them. */
const NarratedThinking: React.FC<{ text: string; active?: boolean }> = ({
  text,
  active = false,
}) => {
  const sections = parseNarratedReasoningSections(text);
  const titledSections = sections.filter((section) => section.title.length > 0);
  const lastTitledSectionIndex = sections.reduce(
    (lastIndex, section, index) => (section.title ? index : lastIndex),
    -1
  );

  if (titledSections.length === 0) {
    return <SimpleMarkdown text={text} compact className={TIMELINE_MARKDOWN_CLASS} />;
  }

  return (
    <div className="flex flex-col gap-2.5">
      {sections.map((section, index) => (
        <div key={`${section.title || "preamble"}-${index}`} className="relative min-w-0">
          {section.title && (
            <>
              <span
                className={cn(
                  "absolute -left-4 top-[6px] block size-[7px] rounded-full ring-2 ring-background",
                  active && index === lastTitledSectionIndex
                    ? "animate-pulse bg-primary"
                    : "bg-primary/60"
                )}
                data-timeline-marker="thinking"
                aria-hidden
              />
              <div className="text-[12.5px] font-semibold leading-snug text-foreground">
                {section.title}
              </div>
            </>
          )}
          {section.content && (
            <SimpleMarkdown
              text={section.content}
              compact
              className={cn(TIMELINE_MARKDOWN_CLASS, section.title && "mt-1")}
            />
          )}
        </div>
      ))}
    </div>
  );
};

/** Pulls the final answer text from a completed research tool result. */
function researchAnswer(tool: ExcelAgentToolView): string | undefined {
  if (!tool.result || typeof tool.result !== "object") return undefined;
  const result = tool.result as Record<string, unknown>;
  const data =
    result.data && typeof result.data === "object"
      ? (result.data as Record<string, unknown>)
      : undefined;
  const answer = result.answer ?? data?.answer;
  return typeof answer === "string" && answer.trim() ? answer.trim() : undefined;
}

/** Collapsible card for a completed research answer. */
const ResultCard: React.FC<{ answer?: string }> = ({ answer }) => {
  const [open, setOpen] = React.useState(false);
  if (!answer) return null;
  return (
    <section className="mt-2 overflow-hidden rounded-lg border border-border/60 bg-background">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 border-0 bg-transparent px-3 py-2 text-left transition-colors hover:bg-muted/25"
      >
        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden />
        <span className="min-w-0 flex-1 truncate text-[12px] font-semibold text-primary">
          Result
        </span>
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform",
            open && "rotate-180"
          )}
          aria-hidden
        />
      </button>
      {open && (
        <div className="px-3 pb-3 pt-1 text-[11.5px] leading-relaxed text-foreground/80">
          <SimpleMarkdown text={answer} compact />
        </div>
      )}
    </section>
  );
};

/**
 * Extracts only user-useful formula text from a generate_formula result. The
 * timeline keeps the structured result for execution, but the disclosure never
 * renders that raw JSON envelope.
 */
function generatedFormulaText(tool: ExcelAgentToolView): string[] {
  if (tool.formulaResults?.length) return tool.formulaResults;
  if (!tool.result || typeof tool.result !== "object") return [];
  const result = tool.result as Record<string, unknown>;
  const entries = Array.isArray(result.results) ? result.results : [result];
  return entries.flatMap((entry) => {
    if (!entry || typeof entry !== "object") return [];
    const record = entry as Record<string, unknown>;
    const formula =
      typeof record.formula === "string" && record.formula.trim()
        ? record.formula.trim()
        : typeof record.formula_template === "string" && record.formula_template.trim()
          ? record.formula_template.trim()
          : undefined;
    return formula ? [formula] : [];
  });
}

/** Generate Formula pill with its returned formula available on click. */
const GenerateFormulaDisclosure: React.FC<{ tool: ExcelAgentToolView }> = ({ tool }) => {
  const [open, setOpen] = React.useState(false);
  const formulas = generatedFormulaText(tool);
  if (formulas.length === 0) return <ToolPill tool={tool} />;
  return (
    <section className="w-full min-w-0" data-generate-formula-disclosure={tool.callId}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-label="Generate formula details"
        className="flex w-full min-w-0 items-center gap-1.5 border-0 bg-transparent p-0 text-left"
      >
        <span className="min-w-0 flex-1">
          <ToolPill tool={tool} />
        </span>
        <ChevronDown
          className={cn(
            "size-3.5 shrink-0 text-muted-foreground transition-transform",
            open && "rotate-180"
          )}
          aria-hidden
        />
      </button>
      {open && (
        <div
          className="mt-1.5 space-y-1.5 rounded-md border border-border/60 bg-background px-3 py-2"
          data-generate-formula-result={tool.callId}
        >
          {formulas.map((formula, index) => (
            <pre
              key={`${tool.callId}-${index}`}
              className="whitespace-pre-wrap break-all font-mono text-[11px] leading-relaxed text-foreground/80"
            >
              {formula}
            </pre>
          ))}
        </div>
      )}
    </section>
  );
};

/**
 * Format a decoded snapshot size for the compact preview caption.
 *
 * @param bytes - Decoded PNG size in bytes.
 * @returns Human-readable byte or kilobyte label.
 */
function snapshotByteLabel(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  return `${(bytes / 1024).toFixed(bytes < 10 * 1024 ? 1 : 0)} KB`;
}

/**
 * Render a snapshot pill with a task-pane-memory-only image disclosure.
 *
 * @param props - Component properties.
 * @param props.tool - Completed snapshot tool view carrying an opaque preview id.
 * @returns Collapsible snapshot preview, or a normal pill when the image expired.
 */
const SnapshotDisclosure: React.FC<{ tool: ExcelAgentToolView }> = ({ tool }) => {
  const [open, setOpen] = React.useState(false);
  const preview = React.useSyncExternalStore(
    subscribeSnapshotPreviews,
    () => getSnapshotPreview(tool.snapshotPreviewId),
    () => undefined,
  );
  if (!preview) return <ToolPill tool={tool} />;
  return (
    <section className="w-full min-w-0" data-snapshot-disclosure={tool.callId}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-label={`Snapshot preview for ${preview.sheetName} ${preview.address}`}
        className="flex w-full min-w-0 items-center gap-1.5 border-0 bg-transparent p-0 text-left"
      >
        <span className="min-w-0 flex-1">
          <ToolPill tool={tool} />
        </span>
        <ChevronDown
          className={cn(
            "size-3.5 shrink-0 text-muted-foreground transition-transform",
            open && "rotate-180",
          )}
        />
      </button>
      {open && (
        <div className="mt-2 overflow-hidden rounded-lg border border-border/70 bg-muted/20">
          <img
            src={preview.objectUrl}
            alt={`Worksheet ${preview.sheetName} range ${preview.address}`}
            className="max-h-96 w-full bg-white object-contain"
          />
          <div className="flex flex-wrap items-center justify-between gap-1.5 border-t border-border/60 px-2.5 py-2 text-[10.5px] text-muted-foreground">
            <span>{preview.sheetName}!{preview.address}</span>
            <span>
              {preview.width}×{preview.height}px · {snapshotByteLabel(preview.byteLength)}
            </span>
          </div>
        </div>
      )}
    </section>
  );
};

/** One minimized meta-agent child query, expandable to its observable stream. */
const DelegatedQueryCard: React.FC<{
  delegation: ExcelAgentDelegationView;
}> = ({ delegation }) => {
  const [open, setOpen] = React.useState(false);
  const status: ExcelAgentToolStatus =
    delegation.status === "running" ? "calling" : delegation.status;
  const tools = delegation.hops.flatMap((hop) => hop.tools);
  const progress =
    delegation.statusText ||
    (status === "calling"
      ? tools.length > 0
        ? `${tools.length} source ${tools.length === 1 ? "step" : "steps"} in progress`
        : "Starting research"
      : `${tools.length} source ${tools.length === 1 ? "step" : "steps"}`);
  const answer = delegation.finalAnswer ?? delegation.liveAnswer.trim();

  return (
    <section className="overflow-hidden rounded-lg border border-border/60 bg-background">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 border-0 bg-transparent px-3 py-2 text-left transition-colors hover:bg-muted/25"
      >
        <StatusIcon status={status} />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[12px] font-semibold text-foreground">
            {delegation.title}
          </span>
          <span className="mt-0.5 block truncate text-[10.5px] text-muted-foreground">
            {progress}
          </span>
        </span>
        <ChevronDown
          className={cn(
            "size-3.5 shrink-0 text-muted-foreground transition-transform",
            open && "rotate-180"
          )}
          aria-hidden
        />
      </button>
      {open && (
        <div className="border-t border-border/50 px-3 py-2.5 text-[11.5px]">
          <div className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
            Query
          </div>
          <div className="mt-1 whitespace-pre-wrap leading-relaxed text-foreground/80">
            {delegation.query}
          </div>
          {tools.length > 0 && (
            <div className="mt-3">
              <div className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
                Activity
              </div>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {tools.map((tool) => (
                  <SubToolPill key={tool.subCallId} tool={tool} />
                ))}
              </div>
            </div>
          )}
          {delegation.hops.some((hop) => hop.thinkingText.trim()) && (
            <div className="mt-3">
              <div className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
                Progress
              </div>
              <div className="mt-1 space-y-1 leading-relaxed text-muted-foreground">
                {delegation.hops.map((hop) =>
                  hop.thinkingText.trim() ? (
                    <SimpleMarkdown key={hop.subHop} text={hop.thinkingText} compact />
                  ) : null
                )}
              </div>
            </div>
          )}
          {answer && (
            <div className="mt-3">
              <div className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
                {delegation.finalAnswer ? "Result" : "Latest output"}
              </div>
              <div className="mt-1 leading-relaxed text-foreground/80">
                <SimpleMarkdown text={answer} compact />
              </div>
            </div>
          )}
          {delegation.citations.length > 0 && (
            <div className="mt-3 text-[10.5px] text-muted-foreground">
              {delegation.citations.length} citation
              {delegation.citations.length === 1 ? "" : "s"}
            </div>
          )}
        </div>
      )}
    </section>
  );
};

/** Shows live nested research and delegated sub-agent activity. */
const NestedResearchTimeline: React.FC<{ sub: ExcelAgentSubAgentView }> = ({ sub }) => {
  return (
    <div className="relative mt-2 ml-4 border-l border-border/60 pl-3">
      <div className="flex flex-col gap-3">
        {sub.delegations?.map((delegation) => (
          <DelegatedQueryCard key={delegation.delegationId} delegation={delegation} />
        ))}
        {sub.hops.map((subHop) => (
          <div key={subHop.subHop} className="min-w-0">
            {subHop.tools.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {subHop.tools.map((tool) => (
                  <SubToolPill key={tool.subCallId} tool={tool} />
                ))}
              </div>
            )}
            {subHop.thinkingText.trim() && (
              <div className="mt-1.5 text-[11.5px] leading-relaxed text-muted-foreground/90">
                <SimpleMarkdown text={subHop.thinkingText} compact />
              </div>
            )}
          </div>
        ))}
        {!sub.finalAnswer && sub.liveAnswer.trim() && (
          <div className="text-[11.5px] leading-relaxed text-muted-foreground">
            <SimpleMarkdown text={sub.liveAnswer} compact />
          </div>
        )}
      </div>
    </div>
  );
};

/** Adds a short user-facing note when a tool call fails. */
const FailureNote: React.FC<{ tool: ExcelAgentToolView }> = ({ tool }) => {
  if (tool.status !== "failed" || !isExcelResearchTool(tool.tool)) return null;
  return (
    <p className="mt-1.5 text-[11px] leading-relaxed text-muted-foreground">
      Source lookup did not complete. The agent should continue only with available evidence.
    </p>
  );
};

/** Parent disclosure that collapses an entire FactSet AI / Finster research call. */
const ResearchToolDisclosure: React.FC<{ tool: ExcelAgentToolView }> = ({ tool }) => {
  const [open, setOpen] = React.useState(tool.status === "failed");
  React.useEffect(() => {
    if (tool.status === "failed") setOpen(true);
  }, [tool.status]);
  const label = getExcelAgentToolDisplayName(tool.tool);
  return (
    <section className="w-full min-w-0" data-research-tool-disclosure={tool.callId}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-label={`${label} details`}
        className="flex w-full min-w-0 items-center gap-1.5 border-0 bg-transparent p-0 text-left"
      >
        <span className="min-w-0 flex-1">
          <ToolPill tool={tool} />
        </span>
        <ChevronDown
          className={cn(
            "size-3.5 shrink-0 text-muted-foreground transition-transform",
            open && "rotate-180"
          )}
          aria-hidden
        />
      </button>
      {open && (
        <div data-research-tool-content={tool.callId}>
          <FailureNote tool={tool} />
          {tool.sub && <NestedResearchTimeline sub={tool.sub} />}
          {tool.status === "completed" && !tool.researchGroupProgress && (
            <ResultCard answer={tool.sub?.finalAnswer ?? researchAnswer(tool)} />
          )}
        </div>
      )}
    </section>
  );
};

const TraceTools: React.FC<{
  tools: ExcelAgentToolView[];
  hasThinking: boolean;
}> = ({ tools, hasThinking }) => {
  if (tools.length === 0) return null;
  return (
    <div className={cn("flex flex-col items-start gap-1.5", hasThinking && "mt-1.5")}>
      {tools.map((tool) => (
        <div key={tool.callId} className="w-full min-w-0">
          {isExcelResearchTool(tool.tool) ? (
            <ResearchToolDisclosure tool={tool} />
          ) : tool.tool === "generate_formula" ? (
            <GenerateFormulaDisclosure tool={tool} />
          ) : tool.tool === "get_snapshot" ? (
            <SnapshotDisclosure tool={tool} />
          ) : (
            <>
              <ToolPill tool={tool} />
              <FailureNote tool={tool} />
            </>
          )}
        </div>
      ))}
    </div>
  );
};

const ReasoningTrace: React.FC<{
  block: TimelineTraceBlock;
  active: boolean;
}> = ({ block, active }) => {
  const [open, setOpen] = React.useState(active);
  React.useEffect(() => {
    setOpen(active);
  }, [active]);
  const hasThinking = block.thinkingText.trim().length > 0;
  return (
    <details
      open={open}
      onToggle={(event) => setOpen(event.currentTarget.open)}
      className="group min-w-0"
      data-timeline-block="trace"
    >
      <summary className="flex cursor-pointer list-none items-center gap-1.5 text-[11.5px] font-medium text-muted-foreground">
        <ChevronDown
          className={cn("size-3.5 transition-transform", open ? "rotate-0" : "-rotate-90")}
          aria-hidden
        />
        <span>{active ? "Thinking" : "Reasoning"}</span>
      </summary>
      <div className="mt-1.5 min-w-0 pl-5">
        {hasThinking && (
          <div className="text-[11.5px] leading-relaxed text-muted-foreground">
            <NarratedThinking text={block.thinkingText} active={active} />
          </div>
        )}
        <TraceTools tools={block.tools} hasThinking={hasThinking} />
      </div>
    </details>
  );
};

/**
 * Renders the executor thought process as a compact vertical timeline, including
 * visible tool calls and nested research activity while streaming.
 */
export const ExcelAgentTimeline: React.FC<{
  timeline: ExcelAgentTimelineState;
  streaming?: boolean;
}> = ({ timeline, streaming = false }) => {
  const blocks = toDisplayBlocks(timeline);
  const activeUserSkills = timeline.activeUserSkills ?? [];
  const hasCallingTool = blocks.some(
    (block) =>
      block.kind === "trace" && block.tools.some((tool) => tool.status === "calling")
  );
  const lastBlock = blocks.at(-1);
  const showWorkingPulse =
    streaming &&
    blocks.length > 0 &&
    !hasCallingTool &&
    lastBlock?.kind !== "assistant";
  // The Skill chips stand alone until the first hop arrives, so an empty row
  // set is only nothing to render when there are no Skills either.
  if (blocks.length === 0 && activeUserSkills.length === 0) return null;

  return (
    <div className="relative pl-4">
      {activeUserSkills.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-1.5">
          {activeUserSkills.map((skill) => (
            <SkillActiveActivity key={skill.id} skill={skill} />
          ))}
        </div>
      )}
      <div className="flex flex-col gap-3">
        {blocks.map((block, index) => {
          if (block.kind === "assistant") {
            return (
              <div
                key={block.key}
                className="min-w-0 text-[13px] leading-relaxed text-foreground"
                data-timeline-block="assistant"
              >
                <SimpleMarkdown text={block.text} compact />
              </div>
            );
          }
          return (
            <ReasoningTrace
              key={block.key}
              block={block}
              active={streaming && index === blocks.length - 1}
            />
          );
        })}
        {showWorkingPulse && <WorkingPulse />}
      </div>
    </div>
  );
};
